import React, { useEffect, useState } from "react";
import useSWR from "swr";
import LoaderSmall from "../../../main/components/loader-small/loader-small";
import { useLayout } from "../../hooks/useLayout";
import ActorApplicationList from "../actorApplicationList/actorApplicationList";
import ApplicationContainer from "../applicationContainer/applicationContainer";

import { Grid, GridItem, Text, VerticalSpacer } from "@innovationnorway/lighthouse-components";
import { ApplicationV2 } from "../../../main/models/application-v2";
import { FetchStatus } from "../../../main/models/fetch-status";
import useWindowSize from "../../../utils/windowsSize";
import { getUserAccounts } from "../../apis/getUserAccounts";
import AddApplicationsCard from "../addApplicationsCard/addApplicationsCard";
import Courses from "../courses/courses";
import EditUserAccountModal from "../editUserAccountModal/editUserAccountModal";
import { InfoBanner } from "../infoBanner/infoBanner";
import InfoCard, { InfoLineProps } from "../infoCard/infoCard";
import "./actorApplications.scss";

interface ActorApplicationsProps {
  currentAccountPublicId?: string;
}

const ActorApplications = ({ currentAccountPublicId }: ActorApplicationsProps) => {
  const { translations, layout } = useLayout();
  const { data: userAccounts, isValidating } = useSWR(`userAccounts`, getUserAccounts);
  const [activeApplication, setActiveApplication] = useState<ApplicationV2>();
  const [openEditUserAccountModal, setOpenEditUserAccountModal] = useState(false);

  const currentAccount = userAccounts?.find((ua) => ua.account.publicId === currentAccountPublicId);

  const windowSize = useWindowSize();
  const breakGridOne = windowSize.width < 1424;
  const breakGridSecond = windowSize.width < 1254;

  useEffect(() => {
    if (userAccounts && userAccounts.length > 0) {
      if (currentAccountPublicId == undefined) currentAccountPublicId = userAccounts[0].account.publicId;

      if (userAccounts && userAccounts.length > 0) {
        setActiveApplication(
          userAccounts.find((ua) => ua.account.publicId === currentAccountPublicId)?.applications[0]
        );
      }
    }
  }, [userAccounts, currentAccountPublicId]);

  const contactInfoLines: InfoLineProps[] = [
    {
      id: translations.chooseAccountEmailInput + currentAccount?.emailAddress,
      textKey: translations.chooseAccountEmailInput,
      value: currentAccount?.emailAddress
    },
    {
      id: translations.chooseAccountMobileInput + currentAccount?.phoneNumber,
      textKey: translations.chooseAccountMobileInput,
      value: currentAccount?.phoneNumber
    },
    {
      id:
        translations.editContactNewsletterConsentLabel +
        (currentAccount?.newsletterConsent ? translations.yes : translations.no),
      textKey: translations.editContactNewsletterConsentLabel,
      value: currentAccount?.newsletterConsent ? translations.yes : translations.no,
      longKey: true
    }
  ];

  return (
    <>
      <div>
        {!isValidating && currentAccount?.fetchStatus != FetchStatus.UnknownFail && currentAccount ? (
          userAccounts && userAccounts?.length < 2 && currentAccount.applications.length < 1 ? (
            <Grid>
              <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
                {currentAccount.fetchStatus === FetchStatus.CoursesFailed ? (
                  <InfoBanner variant="error">
                    <Text>
                      {translations.couldNotFetchCoursesError} {translations.generalErrorContactSupport}
                    </Text>
                  </InfoBanner>
                ) : (
                  currentAccount.courses.length > 1 && (
                    <Courses
                      courses={currentAccount.courses}
                      title={`${translations.recommendedActivitiesFor} ${currentAccount.account.name}`}
                      coursesLink={layout?.baseUrls.courseBaseUrl || ""}
                    />
                  )
                )}
              </GridItem>
              <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
                <VerticalSpacer size="m" />
                <div className="actorApplicationsInfoCard">
                  <InfoCard
                    heading={translations.editContactHeader}
                    infoLines={contactInfoLines}
                    buttonText={translations.change}
                    buttonOnClick={() => {
                      setOpenEditUserAccountModal(true);
                    }}
                  />
                </div>
              </GridItem>
            </Grid>
          ) : (
            <Grid>
              <GridItem
                spanLarge={breakGridOne ? (breakGridSecond ? 5 : 4) : 3}
                spanMedium={breakGridOne ? (breakGridSecond ? 5 : 4) : 3}
                spanSmall={1}
              >
                <Grid>
                  {currentAccount.fetchStatus === FetchStatus.ApplicationsFailed ? (
                    <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
                      <InfoBanner variant="error">
                        <Text>
                          {translations.couldNotFetchApplicationsError} {translations.generalErrorContactSupport}
                        </Text>
                      </InfoBanner>
                    </GridItem>
                  ) : (
                    currentAccount.applications.length > 0 && (
                      <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
                        <Text variant="lead1">{translations.projectListHeader}</Text>
                        <VerticalSpacer size="s" />
                        <ActorApplicationList
                          setActiveApplication={setActiveApplication}
                          activeApplication={activeApplication}
                          applications={
                            userAccounts?.find((ua) => ua.account.publicId === currentAccountPublicId)?.applications ??
                            []
                          }
                        />
                      </GridItem>
                    )
                  )}
                  <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
                    <AddApplicationsCard
                      projectAssistantUrl={currentAccount.projectAssistantUrl}
                      fastForwardUrl={currentAccount.fastForwardUrl}
                    />
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem spanLarge={1} spanMedium={1} spanSmall={1} hideOnSmall>
                <div />
              </GridItem>
              {activeApplication ? (
                <GridItem
                  spanLarge={breakGridOne ? (breakGridSecond ? 6 : 7) : 8}
                  spanMedium={breakGridOne ? (breakGridSecond ? 6 : 7) : 8}
                  spanSmall={3}
                >
                  <ApplicationContainer
                    activeApplication={activeApplication}
                    contactInfoLines={contactInfoLines}
                    setOpenEditContactInfoModal={setOpenEditUserAccountModal}
                    fetchStatus={currentAccount.fetchStatus}
                  />
                </GridItem>
              ) : (
                <GridItem
                  spanLarge={breakGridOne ? (breakGridSecond ? 6 : 7) : 8}
                  spanMedium={breakGridOne ? (breakGridSecond ? 6 : 7) : 8}
                  spanSmall={3}
                >
                  <InfoCard
                    heading={translations.editContactHeader}
                    infoLines={contactInfoLines}
                    buttonText={translations.change}
                    buttonOnClick={() => {
                      setOpenEditUserAccountModal(true);
                    }}
                  />
                </GridItem>
              )}
            </Grid>
          )
        ) : currentAccount?.fetchStatus == FetchStatus.UnknownFail && !isValidating ? (
          <InfoBanner variant="error">
            {translations.loadingFailed} {translations.generalErrorContactSupport}
          </InfoBanner>
        ) : (
          <div className="loader">
            <LoaderSmall />
          </div>
        )}
      </div>
      {openEditUserAccountModal && currentAccount && (
        <EditUserAccountModal
          modalOpen={true}
          onClose={() => setOpenEditUserAccountModal(false)}
          userAccount={currentAccount}
        />
      )}
    </>
  );
};

export default ActorApplications;
